import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Dropdown, Input, Modal, Select, Table, Tag, message } from "antd";
import { CheckCircle2, MoreVertical, PauseCircle, Pencil, PlayCircle, Plus, Search, Trash2, XCircle } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { DOMAIN_ID, ORG_ID } from "../../../constants";
import { approvePolicyAgentless, listADPolicies, policyAction, revokePolicyAgentless } from "../../../service/adPolicyService";
import { deletePolicyJSON } from "../../../service/appService";
import { StatusPill } from "../ad-policies/adPolicyTokens";
import { StatusPill as TonePill } from "../../../design-system/tableUi";
import { radiusActionLabel, radiusActionTone, radiusIdentitySummary, radiusDeviceSummary, serviceTypeLabel } from "./radiusPolicyModel";

function formatUpdated(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch {
    return "—";
  }
}

const DRAFT_KEYS = ["policyDraft.basicPolicyInformation", "editPolicyData", "formSessionData"];
const clearPolicyDrafts = () => DRAFT_KEYS.forEach((k) => localStorage.removeItem(k));

export default function RadiusPoliciesPage() {
  const navigate = useNavigate();
  const [messageApi, messageContext] = message.useMessage();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [modalApi, modalContext] = Modal.useModal();

  const fetchPolicies = useCallback(() => {
    setLoading(true);
    listADPolicies({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyType: "radius", page: 1, limit: 200 },
      onSuccess: (res) => {
        const list = (res?.policies || res?.data || []).filter((p) => (p?.policyType || "").toLowerCase() === "radius");
        setPolicies(list);
        setLoading(false);
      },
      onFailure: () => {
        messageApi.error("Failed to load RADIUS policies");
        setLoading(false);
      },
    });
  }, [messageApi]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const visiblePolicies = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (q && !(p.policyName || "").toLowerCase().includes(q)) return false;
      if (statusFilter && p.status !== statusFilter) return false;
      return true;
    });
  }, [policies, search, statusFilter]);

  const handleCreatePolicy = () => {
    clearPolicyDrafts();
    navigate("/pam/policy/v2/createRadiusPolicy");
  };

  const handleUpdatePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot edit");
      return;
    }
    clearPolicyDrafts();
    localStorage.setItem("editPolicyData", JSON.stringify(row));
    localStorage.setItem("policyDraft.basicPolicyInformation", JSON.stringify(row));
    navigate("/pam/policy/v2/createRadiusPolicy?edit=true", { state: { policy: row } });
  };

  const handleDeletePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id — cannot delete");
      return;
    }
    modalApi.confirm({
      title: `Delete policy "${row.policyName}"?`,
      content: "This cannot be undone. The policy will stop being evaluated immediately.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          deletePolicyJSON({
            payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" deleted`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to delete policy");
              resolve();
            },
          });
        }),
    });
  };

  const handleChangeStatus = (row, actionValue) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    const successMsg = actionValue === "Approved" ? "Policy re-activated" : "Policy paused";
    const failureVerb = actionValue === "Approved" ? "re-activate" : "pause";
    policyAction({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), action: { actionType: "status", actionIds: [id], actionValue } },
      onSuccess: () => {
        messageApi.success(successMsg);
        fetchPolicies();
      },
      onFailure: () => messageApi.error(`Failed to ${failureVerb} policy`),
    });
  };

  const handleApprovePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    approvePolicyAgentless({
      payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
      onSuccess: () => {
        messageApi.success(`Policy "${row.policyName}" approved`);
        fetchPolicies();
      },
      onFailure: () => messageApi.error("Failed to approve policy"),
    });
  };

  const handleRevokePolicy = (row) => {
    const id = row.id || row.policyId;
    if (!id) {
      messageApi.error("Missing policy id");
      return;
    }
    modalApi.confirm({
      title: `Revoke policy "${row.policyName}"?`,
      content: "The policy will stop being evaluated until it's approved again.",
      okText: "Revoke",
      okButtonProps: { danger: true },
      onOk: () =>
        new Promise((resolve) => {
          revokePolicyAgentless({
            payload: { orgId: ORG_ID(), tenantId: DOMAIN_ID(), policyId: id },
            onSuccess: () => {
              messageApi.success(`Policy "${row.policyName}" revoked`);
              fetchPolicies();
              resolve();
            },
            onFailure: () => {
              messageApi.error("Failed to revoke policy");
              resolve();
            },
          });
        }),
    });
  };

  const columns = [
    {
      title: "Policy name",
      key: "policyName",
      render: (_, row) => <span style={{ fontFamily: AN_FONT, fontWeight: 500, color: AN_COLORS.ink }}>{row.policyName}</span>,
    },
    {
      title: "Action",
      key: "action",
      width: 130,
      // mfa_required → Require MFA, allow → Allow, block → Block.
      render: (_, row) => {
        const flow = row.policyJson?.policyFlow;
        return <TonePill tone={radiusActionTone(flow)} label={radiusActionLabel(flow)} />;
      },
    },
    {
      title: "Applies to",
      key: "identity",
      render: (_, row) => <span style={{ fontSize: 12.5 }}>{radiusIdentitySummary(row.policyJson)}</span>,
    },
    {
      title: "Devices",
      key: "devices",
      render: (_, row) => <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{radiusDeviceSummary(row.policyJson)}</span>,
    },
    {
      title: "Service types",
      key: "serviceTypes",
      render: (_, row) => {
        const st = row.policyJson?.radius?.serviceTypes || [];
        if (!st.length) return <span style={{ color: AN_COLORS.muted }}>Any</span>;
        return (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {st.map((s) => (
              <Tag key={s} style={{ marginInlineEnd: 0, color: AN_COLORS.ink2, background: AN_COLORS.tint, borderColor: AN_COLORS.line }}>
                {serviceTypeLabel(s)}
              </Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      width: 110,
      render: (_, row) => <StatusPill status={row.status} />,
    },
    {
      title: "Updated",
      key: "updated",
      width: 160,
      render: (_, row) => (
        <span style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
          {formatUpdated(row.updatedAt)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 44,
      align: "center",
      render: (_, row) => {
        const isApproved = row.status === "Approved";
        const items = [{ key: "update", icon: <Pencil size={13} />, label: "Update policy", onClick: () => handleUpdatePolicy(row) }];
        if (!isApproved) items.push({ key: "approve", icon: <CheckCircle2 size={13} />, label: "Approve", onClick: () => handleApprovePolicy(row) });
        if (isApproved) items.push({ key: "pause", icon: <PauseCircle size={13} />, label: "Pause", onClick: () => handleChangeStatus(row, "Suspended") });
        else if (row.status === "Suspended") items.push({ key: "reactivate", icon: <PlayCircle size={13} />, label: "Re-activate", onClick: () => handleChangeStatus(row, "Approved") });
        if (isApproved) items.push({ key: "revoke", icon: <XCircle size={13} />, label: "Revoke", onClick: () => handleRevokePolicy(row) });
        items.push({ type: "divider" });
        items.push({ key: "delete", danger: true, icon: <Trash2 size={13} />, label: "Delete policy", onClick: () => handleDeletePolicy(row) });
        return (
          <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }}>
            <button
              type="button"
              aria-label="Row actions"
              style={{ width: 28, height: 28, border: "none", background: "transparent", color: AN_COLORS.muted, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 6 }}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ minHeight: "calc(100vh - 64px)", background: AN_COLORS.bgWarm, color: AN_COLORS.ink, fontFamily: AN_FONT, padding: "clamp(1rem, 3vw, 1.375rem) clamp(1rem, 3vw, 1.75rem)" }}>
      {messageContext}
      {modalContext}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", marginBottom: 18, gap: 16 }}>
        <div>
          <div style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 6 }}>
            Admin · Policies · RADIUS
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4, color: AN_COLORS.ink }}>RADIUS Policies</h1>
          <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>
            Require MFA when a directory user reaches a VPN, switch or firewall over RADIUS.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" icon={<Plus size={13} strokeWidth={2.2} />} onClick={handleCreatePolicy}>
            New policy
          </Button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ background: "var(--an-surface)", border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(12rem, 1fr))", gap: 12, alignItems: "end" }}>
          <label style={{ display: "block" }}>
            <div style={{ fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2, marginBottom: 6 }}>Search by name</div>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} prefix={<Search size={14} color={AN_COLORS.muted} />} placeholder="vpn-…" style={{ height: 36 }} />
          </label>
          <label style={{ display: "block" }}>
            <div style={{ fontSize: 11.5, fontWeight: 500, color: AN_COLORS.ink2, marginBottom: 6 }}>Status</div>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              placeholder="Any status"
              style={{ width: "100%", height: 36 }}
              options={[
                { value: "Approved", label: "Active" },
                { value: "Suspended", label: "Paused" },
              ]}
            />
          </label>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
          <span style={{ marginLeft: "auto", fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_MONO }}>
            Showing {visiblePolicies.length} of {policies.length}
          </span>
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "var(--an-surface)", border: `1px solid ${AN_COLORS.line}`, borderRadius: 10, overflow: "hidden" }}>
        <Table
          rowKey={(row) => row.id || row.policyId || row.policyName}
          columns={columns}
          dataSource={visiblePolicies}
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: loading ? "Loading…" : "No RADIUS policies yet." }}
        />
      </div>
    </div>
  );
}
